<template>
  <!-- Cine nu poate accepta în numele companiei primește o notificare, nu un
       blocaj: guardul de backend lasă `GET/HEAD/OPTIONS` să treacă, deci citirea
       și exportul rămân disponibile. Un dialog `persistent` fără acțiuni i-ar fi
       închis complet aplicația, contrazicând chiar mesajul din interiorul lui. -->
  <div v-if="visible && !canAccept" class="legal-gate-banner" role="status">
    <v-icon size="18" class="legal-gate-banner__icon">mdi-file-document-alert-outline</v-icon>
    <span class="legal-gate-banner__text">{{ $t("legal.gate.adminOnly") }}</span>
  </div>

  <v-dialog
    :model-value="visible && canAccept"
    persistent
    max-width="640"
    scrollable
  >
    <v-card class="legal-gate">
      <v-card-title class="legal-gate__title">
        <v-icon color="warning" class="mr-2">mdi-file-document-check-outline</v-icon>
        <span>{{ $t("legal.gate.title") }}</span>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <p class="mb-3">{{ $t("legal.gate.body") }}</p>

        <!-- Citirea și exportul rămân deschise: spunem asta explicit, ca ecranul
             să nu pară o blocare a accesului la datele proprii. -->
        <v-alert type="info" density="compact" class="mb-4">
          {{ $t("legal.gate.readStillAllowed") }}
        </v-alert>

        <template v-if="canAccept">
          <ul class="legal-gate__links">
            <li><a :href="dpaUrl" target="_blank" rel="noopener">{{ $t("legal.gate.readDpa") }}</a></li>
            <li><a :href="termsUrl" target="_blank" rel="noopener">{{ $t("auth.registration.legal.terms") }}</a></li>
            <li><a :href="privacyUrl" target="_blank" rel="noopener">{{ $t("auth.registration.legal.privacy") }}</a></li>
          </ul>

          <v-text-field
            v-model="companyIdno"
            :label="$t('auth.registration.fields.companyIdno')"
            :hint="$t('auth.registration.fields.companyIdnoHint')"
            persistent-hint
            inputmode="numeric"
            class="mb-2"
          />

          <v-checkbox v-model="accepted" color="primary" hide-details>
            <template #label><span>{{ $t("legal.gate.acceptLabel") }}</span></template>
          </v-checkbox>
          <v-checkbox v-model="representative" color="primary" hide-details>
            <template #label><span>{{ $t("auth.registration.legal.representative", { company: companyName }) }}</span></template>
          </v-checkbox>

          <v-alert v-if="errorMessage" type="error" density="compact" class="mt-3">
            {{ errorMessage }}
          </v-alert>
        </template>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn color="primary" variant="flat" :loading="submitting" :disabled="!canSubmit" @click="submit">
          {{ $t("legal.gate.submit") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import { ACTIVE_LEGAL_VERSIONS } from "@/Utile/legalVersions.mjs";
import { buildDpaDocumentUrl } from "@/Utile/legalDocumentUrls.mjs";

// Poarta de reacceptare pentru conturile create înainte de pachetul juridic
// `1.0` (REG-067). Blocantă pentru administratorul companiei, informativă
// pentru ceilalți utilizatori, care nu pot accepta în numele companiei.
export default {
  name: "LegalAcceptanceGate",
  data() {
    return {
      required: false,
      accepted: false,
      representative: false,
      companyIdno: "",
      submitting: false,
      errorMessage: "",
    };
  },
  computed: {
    visible() { return this.required; },
    locale() { return this.$i18n.locale; },
    canAccept() {
      const role = String(localStorage.getItem("user-role") || "");
      return role === "admin" || role === "miniadmin";
    },
    canSubmit() { return this.accepted && this.representative && !this.submitting; },
    companyName() {
      return localStorage.getItem("company-name") || this.$t("auth.registration.legal.representativeFallback");
    },
    dpaUrl() { return buildDpaDocumentUrl({ version: ACTIVE_LEGAL_VERSIONS.dpa, locale: this.locale }); },
    termsUrl() { return `https://miridar.com/${this.locale}/legal/terms/v${ACTIVE_LEGAL_VERSIONS.terms}/`; },
    privacyUrl() { return `https://miridar.com/${this.locale}/legal/gdpr/v${ACTIVE_LEGAL_VERSIONS.privacy}/`; },
  },
  created() { this.checkStatus(); },
  methods: {
    async checkStatus() {
      try {
        const { data } = await axios.get("/legal/acceptance/status");
        this.required = data?.required === true;
      } catch {
        // Un eșec de verificare nu are voie să blocheze aplicația.
        this.required = false;
      }
    },
    async submit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.errorMessage = "";
      try {
        await axios.post("/legal/acceptance", {
          legal: {
            termsVersion: ACTIVE_LEGAL_VERSIONS.terms,
            termsAccepted: true,
            privacyVersion: ACTIVE_LEGAL_VERSIONS.privacy,
            privacyAcknowledged: true,
            dpaVersion: ACTIVE_LEGAL_VERSIONS.dpa,
            dpaAccepted: true,
            declaredRepresentative: true,
          },
          companyIdno: this.companyIdno.trim() || undefined,
          locale: this.locale,
        });
        this.required = false;
      } catch (error) {
        this.errorMessage = error?.response?.data?.fields
          ? this.$t("legal.gate.errors.validation")
          : this.$t("legal.gate.errors.server");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.legal-gate-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  color: var(--app-secondary-soft-text);
  background: var(--app-warm-highlight-soft-bg);
  border-bottom: 1px solid var(--app-warm-highlight-soft-border);
  font-size: 0.88rem;
}

.legal-gate-banner__icon { flex-shrink: 0; }

.legal-gate-banner__text { line-height: 1.4; }

.legal-gate__title { display: flex; align-items: center; }
.legal-gate__links { margin: 0 0 16px; padding-left: 18px; }
.legal-gate__links a { color: rgb(var(--v-theme-primary)); font-weight: 600; }
</style>
